import { useCallback, useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useLocation, useParams } from 'react-router-dom'
import { NetworkStatus, useQuery } from '@apollo/client'
import { observer } from 'mobx-react'

import { OrdersPreviewColumn } from 'Modules/orders/components/OrdersPreviewColumn/OrdersPreviewColumn'
import { OrdersTable } from 'Modules/orders/components/OrdersTable/OrdersTable'

import { Card } from 'Components/_theme'
import { ContentSidebar } from 'Components/ContentSidebar/ContentSidebar'
import { FaqCard } from 'Components/FaqCard/FaqCard'
import { ImpactTracker } from 'Components/ImpactTracker/ImpactTracker'
import { ListSearch } from 'Components/ListSearch/ListSearch'
import { ObserveMeOnceIAmMounted } from 'Components/ObserveMeOnceIAmMounted/ObserveMeOnceIAmMounted'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageHeader } from 'Components/PageHeader/PageHeader'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import { useIsOrganisation } from 'Hooks'

import { getQueryObject, toggleIntercomVisibility } from 'Utilities'

import { PAGINATION } from 'Constants/constants'
import {
  getEmployeeRequests,
  getEmployeeRequests_employee_caseitems,
  getEmployeeRequestsVariables,
} from 'Constants/graphql/queries/listing/__generated__/getEmployeeRequests'
import {
  getOrganisationRequests,
  getOrganisationRequests_organisation_caseItems,
  getOrganisationRequestsVariables,
} from 'Constants/graphql/queries/listing/__generated__/getOrganisationRequests'
import {
  GET_EMPLOYEE_REQUESTS,
  GET_ORGANISATION_REQUESTS,
} from 'Constants/graphql/queries/listing/GetRequests'
import { PortalSectionEnum } from 'Constants/portalSection'

export const Orders = observer(() => {
  const [selectedEntityId, setSelectedEntityId] = useState('')
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)
  const [isPreviewColumnOpen, setIsPreviewColumnOpen] = useState(false)
  const isOrganisation = useIsOrganisation()

  const { employeeId, organisationId } = useParams<{
    employeeId?: string
    organisationId?: string
  }>()
  const { search: locationSearch } = useLocation()
  const parsedSearch = getQueryObject(locationSearch)

  const { loading, data, error, fetchMore, refetch, networkStatus } = useQuery<
    getOrganisationRequests | getEmployeeRequests,
    getOrganisationRequestsVariables | getEmployeeRequestsVariables
  >(isOrganisation ? GET_ORGANISATION_REQUESTS : GET_EMPLOYEE_REQUESTS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      after: PAGINATION.AFTER,
      first: PAGINATION.ITEMS_PER_PAGE,
      id: organisationId! ?? employeeId!,
      searchterm: (parsedSearch?.searchTerm as string) ?? null,
    },
  })

  const getCaseItems = (
    data: getOrganisationRequests | getEmployeeRequests,
  ):
    | getOrganisationRequests_organisation_caseItems
    | getEmployeeRequests_employee_caseitems
    | null =>
    isOrganisation
      ? (data as getOrganisationRequests).organisation.caseItems
      : (data as getEmployeeRequests).employee.caseitems

  const handleOpenPreviewColumn = useCallback(
    (id: string, rowIndex: number | null) => {
      toggleIntercomVisibility(false)

      setSelectedEntityId(id)
      setSelectedRowIndex(rowIndex)
      setIsPreviewColumnOpen(true)
    },
    [],
  )

  const handleClosePreviewColumn = useCallback(() => {
    toggleIntercomVisibility(true)

    setIsPreviewColumnOpen(false)
    setSelectedRowIndex(null)
  }, [])

  const handleSearch = useCallback(
    (value: string) => {
      refetch({ searchterm: value || null })
    },
    [refetch],
  )

  const onLoadMore = () => {
    if (data)
      fetchMore({
        variables: {
          after: getCaseItems(data)?.pageInfo.endCursor,
        },
      })
  }

  const intersectionObserver = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      onLoadMore()
    }
  })

  useEffect(() => {
    refetch({ searchterm: (parsedSearch?.searchTerm as string) ?? null })
  }, [refetch, parsedSearch?.searchTerm])

  if (error) {
    return <PageErrorState />
  }

  if (loading && !data) return <PageLoadingState />
  if (!data) return <PageErrorState />

  const orders = getCaseItems(data)?.caseItems?.filter(value => !!value)
  const totalCaseItems = getCaseItems(data)?.totalCount
  const hasNextPage = getCaseItems(data)?.pageInfo.hasNextPage

  return (
    <Container className="pt-6" fluid>
      <div>
        <PageHeader portalSection={PortalSectionEnum.Orders} />
      </div>

      <Row>
        <Col xl={12} xxl={8}>
          <Card className="mb-4">
            <ListSearch
              handleSearch={handleSearch}
              totalCount={totalCaseItems ?? 0}
            />

            <Row>
              <Col>
                <OrdersTable
                  isActiveEmployee={!isOrganisation}
                  isLoading={loading}
                  isSearching={networkStatus === NetworkStatus.setVariables}
                  onOpenPreviewColumn={handleOpenPreviewColumn}
                  orders={orders ?? []}
                  selectedRowIndex={selectedRowIndex}
                />
              </Col>
            </Row>
          </Card>

          <Row className="p-1">
            <Col>
              {hasNextPage && !loading && (
                <ObserveMeOnceIAmMounted
                  intersectionObserver={intersectionObserver}
                />
              )}
            </Col>
          </Row>
        </Col>

        <ContentSidebar
          isPreviewColumnShown={isPreviewColumnOpen}
          renderStickyColumnSideContent={
            <>
              <ImpactTracker />
              <FaqCard />
            </>
          }
        >
          <OrdersPreviewColumn
            entityId={selectedEntityId}
            isOpen={isPreviewColumnOpen}
            onClosePreviewColumn={handleClosePreviewColumn}
          />
        </ContentSidebar>
      </Row>
    </Container>
  )
})
